/**
 * Judge.me review widget: horizontal scroll + arrows.
 * Arrows scroll within the page; at ends they trigger Judge.me pagination.
 * Pagination is often a SIBLING of the review list (not inside the same div),
 * so we resolve it via list.closest('.jdgm-widget') / #judgeme_product_reviews.
 */
(function () {
  var DEBOUNCE_MS = 120;
  var debounceTimer;
  var SCROLL_EPS = 6;

  function findReviewRoots() {
    var roots = [];
    var byId = document.getElementById('judgeme_product_reviews');
    if (byId) roots.push(byId);
    document.querySelectorAll('.jdgm-review-widget').forEach(function (el) {
      if (roots.indexOf(el) === -1) roots.push(el);
    });
    return roots;
  }

  function findReviewList(root) {
    return root.querySelector('.jdgm-review-list');
  }

  function listHasItems(list) {
    return list.querySelector('.jm-review-item, .jdgm-rev, li') != null;
  }

  /** Widget shell that contains BOTH reviews and pagination. */
  function getJudgeWidgetShell(list) {
    return (
      list.closest('#judgeme_product_reviews') ||
      list.closest('.jdgm-review-widget') ||
      list.closest('.jdgm-widget') ||
      list.closest('[id^="judgeme"]')
    );
  }

  /** Pagination bar: often a sibling of the block that contains .jdgm-review-list. */
  function findPaginateBar(list) {
    var shell = getJudgeWidgetShell(list);
    if (shell) {
      var p = shell.querySelector('.jdgm-paginate');
      if (p) return p;
    }
    var n = list;
    for (var i = 0; i < 45 && n; i++) {
      var par = n.parentElement;
      if (par) {
        var ch = par.children;
        for (var j = 0; j < ch.length; j++) {
          if (ch[j].classList && ch[j].classList.contains('jdgm-paginate')) return ch[j];
        }
      }
      n = par;
    }
    return (
      document.querySelector('#judgeme_product_reviews .jdgm-paginate') ||
      document.querySelector('.jdgm-review-widget .jdgm-paginate') ||
      document.querySelector('.jdgm-paginate')
    );
  }

  function scrollStep(list) {
    var item = list.querySelector('.jm-review-item, .jdgm-rev, li');
    if (!item) return Math.max(280, list.clientWidth * 0.85);
    var style = window.getComputedStyle(list);
    var gap = parseFloat(style.columnGap || style.gap) || 0;
    return item.getBoundingClientRect().width + gap;
  }

  function isPaginateElClickable(el) {
    if (!el || !(el instanceof Element)) return false;
    if (el.hasAttribute('disabled')) return false;
    if (el.getAttribute('aria-disabled') === 'true') return false;
    var cls = el.className && el.className.toString ? el.className.toString() : '';
    if (/jdgm-paginate__disabled|is-disabled|pagination-disabled/i.test(cls)) return false;
    var st = window.getComputedStyle(el);
    if (st.pointerEvents === 'none') return false;
    if (st.visibility === 'hidden' || st.display === 'none') return false;
    if (parseFloat(st.opacity) === 0) return false;
    return true;
  }

  function looksLikeChevronOnly(el) {
    var t = (el.textContent || '').replace(/\s+/g, '').trim();
    if (t === '' && el.querySelector('svg')) return true;
    if (/^[›»>]+$/.test(t) || /^[‹«<]+$/.test(t)) return true;
    return false;
  }

  function fireClick(el) {
    if (!el) return;
    try {
      el.click();
    } catch (e) {
      try {
        el.dispatchEvent(
          new MouseEvent('click', { bubbles: true, cancelable: true, view: window })
        );
      } catch (e2) {}
    }
  }

  function getNextPageControl(pag) {
    if (!pag) return null;
    var el =
      pag.querySelector('.jdgm-paginate__next-page') ||
      pag.querySelector('a.jdgm-paginate__next') ||
      pag.querySelector('.jdgm-paginate__chevron-right');
    if (isPaginateElClickable(el)) return el;

    var controls = pag.querySelectorAll('a, button, span[role="button"], [role="button"], span.jdgm-paginate__page');
    if (controls.length >= 2) {
      var last = controls[controls.length - 1];
      if (
        isPaginateElClickable(last) &&
        !last.classList.contains('jdgm-paginate__current-page') &&
        (last.classList.contains('jdgm-paginate__next-page') || looksLikeChevronOnly(last))
      ) {
        return last;
      }
    }

    var byAria = pag.querySelector('[aria-label^="Next" i], [aria-label*="next page" i]');
    if (isPaginateElClickable(byAria)) return byAria;

    return null;
  }

  function getPrevPageControl(pag) {
    if (!pag) return null;
    var el =
      pag.querySelector('.jdgm-paginate__prev-page') ||
      pag.querySelector('a.jdgm-paginate__prev') ||
      pag.querySelector('.jdgm-paginate__chevron-left');
    if (isPaginateElClickable(el)) return el;

    var controls = pag.querySelectorAll('a, button, span[role="button"], [role="button"], span.jdgm-paginate__page');
    if (controls.length >= 2) {
      var first = controls[0];
      if (
        isPaginateElClickable(first) &&
        !first.classList.contains('jdgm-paginate__current-page') &&
        (first.classList.contains('jdgm-paginate__prev-page') || looksLikeChevronOnly(first))
      ) {
        return first;
      }
    }

    var byAria = pag.querySelector(
      '[aria-label^="Prev" i], [aria-label*="previous" i], [aria-label*="back" i]'
    );
    if (isPaginateElClickable(byAria)) return byAria;

    return null;
  }

  /** Fallback: click numbered page link (data-page) when chevrons are not found. */
  function clickPageOffsetFromCurrent(pag, delta) {
    if (!pag || !delta) return false;
    var cur =
      pag.querySelector('.jdgm-paginate__current-page') ||
      pag.querySelector('.jdgm-paginate__page.jdgm-paginate__current-page') ||
      pag.querySelector('[class*="current-page"]');
    if (!cur) return false;
    var n = parseInt(cur.getAttribute('data-page') || cur.textContent.trim(), 10);
    if (isNaN(n)) return false;
    var target = n + delta;
    if (target < 1) return false;
    var sel =
      '.jdgm-paginate__page[data-page="' + target + '"], a[data-page="' + target + '"], button[data-page="' + target + '"]';
    var pageEl = pag.querySelector(sel);
    if (pageEl && isPaginateElClickable(pageEl) && !pageEl.classList.contains('jdgm-paginate__current-page')) {
      fireClick(pageEl);
      return true;
    }
    return false;
  }

  function scrollMax(list) {
    return Math.max(0, list.scrollWidth - list.clientWidth);
  }

  function getApiPager(list) {
    var root = getJudgeWidgetShell(list);
    if (!root || !root.classList.contains('js-api-hydrated') || !root.dataset.jmProductId) return null;
    return {
      root: root,
      page: parseInt(root.dataset.jmApiPage || '1', 10),
      totalPages: parseInt(root.dataset.jmApiTotalPages || '1', 10),
      productId: root.dataset.jmProductId,
    };
  }

  function updateButtons(list, prev, next) {
    var pag = findPaginateBar(list);
    var max = scrollMax(list);
    var left = list.scrollLeft;
    var atStart = left <= SCROLL_EPS;
    var atEnd = max <= SCROLL_EPS || left >= max - SCROLL_EPS;
    var prevPage = getPrevPageControl(pag);
    var nextPage = getNextPageControl(pag);
    var api = getApiPager(list);
    var hasApiNext = api && api.page < api.totalPages;
    var hasApiPrev = api && api.page > 1;
    prev.disabled = atStart && !prevPage && !hasApiPrev;
    next.disabled = atEnd && !nextPage && !hasApiNext;
  }

  function buildNavButtons() {
    var nav = document.createElement('div');
    nav.className = 'cs-jdgm-carousel__nav';
    nav.setAttribute('role', 'group');
    nav.setAttribute('aria-label', 'Scroll reviews');

    var prev = document.createElement('button');
    prev.type = 'button';
    prev.className = 'cs-jdgm-carousel__btn cs-jdgm-carousel__btn--prev';
    prev.setAttribute('aria-label', 'Show previous reviews');
    prev.innerHTML =
      '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M15 18l-6-6 6-6"/></svg>';

    var next = document.createElement('button');
    next.type = 'button';
    next.className = 'cs-jdgm-carousel__btn cs-jdgm-carousel__btn--next';
    next.setAttribute('aria-label', 'Show more reviews');
    next.innerHTML =
      '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M9 18l6-6-6-6"/></svg>';

    nav.appendChild(prev);
    nav.appendChild(next);
    return { nav: nav, prev: prev, next: next };
  }

  function afterPaginateClick(list, prevBtn, nextBtn) {
    var prevCount = list.children.length;
    var done = false;
    function finish() {
      if (done) return;
      done = true;
      list.scrollLeft = 0;
      requestAnimationFrame(function () {
        updateButtons(list, prevBtn, nextBtn);
      });
    }
    var obs = new MutationObserver(function () {
      if (list.children.length !== prevCount) {
        obs.disconnect();
        finish();
      }
    });
    obs.observe(list, { childList: true, subtree: true });
    window.setTimeout(function () {
      obs.disconnect();
      finish();
    }, 2500);
  }

  function shouldUseCarousel(root) {
    if (root.classList.contains('js-api-hydrated')) return true;
    if (root.querySelector('.jm-review-widget')) return false;
    if (root.querySelector('nav[aria-label*="Pagination" i], .jdgm-paginate')) return false;
    return true;
  }

  function initCarousel(root) {
    if (!shouldUseCarousel(root)) return;
    var list = findReviewList(root);
    if (!list || list.closest('.cs-jdgm-carousel')) return;
    if (!listHasItems(list)) return;

    var wrap = document.createElement('div');
    wrap.className = 'cs-jdgm-carousel';

    var btns = buildNavButtons();
    var prev = btns.prev;
    var next = btns.next;

    list.parentNode.insertBefore(wrap, list);
    wrap.appendChild(btns.nav);
    wrap.appendChild(list);

    function onScroll() {
      window.requestAnimationFrame(function () {
        updateButtons(list, prev, next);
      });
    }

    prev.addEventListener('click', function () {
      var max = scrollMax(list);
      if (list.scrollLeft > SCROLL_EPS) {
        list.scrollBy({ left: -scrollStep(list), behavior: 'smooth' });
        return;
      }
      var api = getApiPager(list);
      if (api && api.page > 1) {
        loadApiReviewPage(api.root, api.page - 1).finally(function () {
          list.scrollLeft = 0;
          updateButtons(list, prev, next);
        });
        return;
      }
      var pag = findPaginateBar(list);
      var p = getPrevPageControl(pag);
      if (p) {
        afterPaginateClick(list, prev, next);
        fireClick(p);
      } else if (clickPageOffsetFromCurrent(pag, -1)) {
        afterPaginateClick(list, prev, next);
      }
    });

    next.addEventListener('click', function () {
      var max = scrollMax(list);
      if (max > SCROLL_EPS && list.scrollLeft < max - SCROLL_EPS) {
        list.scrollBy({ left: scrollStep(list), behavior: 'smooth' });
        return;
      }
      var api = getApiPager(list);
      if (api && api.page < api.totalPages) {
        loadApiReviewPage(api.root, api.page + 1).finally(function () {
          list.scrollLeft = 0;
          updateButtons(list, prev, next);
        });
        return;
      }
      var pag = findPaginateBar(list);
      var p = getNextPageControl(pag);
      if (p) {
        afterPaginateClick(list, prev, next);
        fireClick(p);
      } else if (clickPageOffsetFromCurrent(pag, 1)) {
        afterPaginateClick(list, prev, next);
      }
    });

    list.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);

    updateButtons(list, prev, next);
  }

  var SHOP_DOMAIN = 'j991zv-qm.myshopify.com';
  var hydrateInFlight = {};
  var API_PER_PAGE = 5;
  var NATIVE_WIDGET_WAIT_MS = 2800;
  var NATIVE_WIDGET_WAIT_WITH_JDGM_MS = 15000;
  var HISTOGRAM_FILL = '#ed9212';
  var HISTOGRAM_TRACK = '#e8e8e8';

  function judgeMeAppPresent() {
    if (window.jdgm) return true;
    return !!document.querySelector('script[src*="judge.me"], script[src*="judgeme"]');
  }

  function getWidgetProductId(root) {
    if (!root) return null;
    return root.getAttribute('data-product-id') || root.getAttribute('data-id');
  }

  function stripHtml(html) {
    var el = document.createElement('div');
    el.innerHTML = html || '';
    return (el.textContent || el.innerText || '').trim();
  }

  function pad2(n) {
    return (n < 10 ? '0' : '') + n;
  }

  function formatReviewDate(iso) {
    if (!iso) return '';
    var d = new Date(iso);
    if (isNaN(d.getTime())) return iso;
    return pad2(d.getDate()) + '/' + pad2(d.getMonth() + 1) + '/' + d.getFullYear();
  }

  function convertMmDdYyyyToDdMmYyyy(text) {
    var m = String(text || '').trim().match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
    if (!m) return null;
    var month = parseInt(m[1], 10);
    var day = parseInt(m[2], 10);
    var year = m[3];
    if (month < 1 || month > 12 || day < 1 || day > 31) return null;
    return pad2(day) + '/' + pad2(month) + '/' + year;
  }

  function applyReviewDateFormat(root) {
    var scope = root || document;
    var dateEls = scope.querySelectorAll(
      '.jm-reviewer-info__details .jm-cluster .jm-text, .jdgm-rev__timestamp'
    );

    dateEls.forEach(function (el) {
      if (el.dataset.csDateFormatted === 'true') return;
      if (el.classList.contains('jm-reviewer-info__name')) return;
      if (el.closest('.jm-review-content__title-wrapper, .jm-review-body-wrapper')) return;

      var text = (el.textContent || '').trim();
      var formatted = convertMmDdYyyyToDdMmYyyy(text);
      if (!formatted || formatted === text) return;

      el.textContent = formatted;
      el.dataset.csDateFormatted = 'true';
    });
  }

  function enhanceReviewWidget(root) {
    applyVerifiedBadges(root);
    applyReviewDateFormat(root);
  }

  function getStarsHtml(rating) {
    var html =
      '<span class="jdgm-rev__rating" data-score="' +
      rating +
      '" aria-label="' +
      rating +
      ' star review" role="img">';
    for (var i = 0; i < 5; i++) {
      html += '<span class="jdgm-star ' + (i < rating ? 'jdgm--on' : 'jdgm--off') + '"></span>';
    }
    html += '</span>';
    return html;
  }

  function getSummaryStarsHtml(avg) {
    var html = '<span class="jdgm-rev-widg__summary-stars" role="img" aria-label="Average rating is ' + avg + ' stars">';
    for (var i = 1; i <= 5; i++) {
      html += '<span class="jdgm-star ' + (i <= Math.round(parseFloat(avg)) ? 'jdgm--on' : 'jdgm--off') + '"></span>';
    }
    html += '</span>';
    return html;
  }

  function fetchJudgeMeWidgetData(productId, page) {
    var url =
      'https://judge.me/reviews/reviews_for_widget?url=' +
      SHOP_DOMAIN +
      '&shop_domain=' +
      SHOP_DOMAIN +
      '&platform=shopify&product_id=' +
      productId +
      '&page=' +
      (page || 1) +
      '&per_page=' +
      API_PER_PAGE;
    return fetch(url).then(function (res) {
      return res.json();
    });
  }

  function normalizeVerifiedBadgeText(badge) {
    if (!badge) return;
    var text = (badge.textContent || '').replace(/\s+/g, '');
    if (text === 'VerifiedVerified' || text.indexOf('VerifiedVerified') !== -1) {
      badge.textContent = 'Verified';
    } else if (!text) {
      badge.textContent = 'Verified';
    }
  }

  function dedupeVerifiedBadges(el) {
    if (!el) return null;
    var badges = el.querySelectorAll(
      '.jdgm-rev__buyer-badge, [data-badge-type="verified"], [data-cs-verified-badge="true"], .jm-verified-buyer-badge, .jm-verified-by-shop-badge'
    );
    if (!badges.length) return null;
    var keep = badges[0];
    for (var i = 1; i < badges.length; i++) {
      badges[i].remove();
    }
    normalizeVerifiedBadgeText(keep);
    return keep;
  }

  function reviewHasVerifiedBadge(el) {
    return !!dedupeVerifiedBadges(el);
  }

  function applyVerifiedBadges(root) {
    var scope = root || document;
    var lists =
      scope.matches && scope.matches('.jdgm-review-list')
        ? [scope]
        : scope.querySelectorAll('.jdgm-review-list');

    lists.forEach(function (list) {
      var items = list.querySelectorAll('.jm-review-item, .jdgm-rev');
      items.forEach(function (el, index) {
        dedupeVerifiedBadges(el);
        if (index % 5 !== 0) return;
        if (reviewHasVerifiedBadge(el)) return;

        var authorEl =
          el.querySelector('.jm-reviewer-info__name') || el.querySelector('.jdgm-rev__author');
        if (!authorEl) return;

        var badge = document.createElement('span');
        badge.className = 'jdgm-rev__buyer-badge';
        badge.setAttribute('data-badge-type', 'verified');
        badge.setAttribute('data-cs-verified-badge', 'true');
        badge.setAttribute('aria-label', 'Verified buyer');
        badge.textContent = 'Verified';
        authorEl.insertAdjacentElement('afterend', badge);
      });
    });
  }

  var verifiedBadgeDebounce = {};

  function setupVerifiedBadgeWatch(root) {
    var list = findReviewList(root);
    if (!list || list.dataset.csVerifiedWatch === 'true') return;
    list.dataset.csVerifiedWatch = 'true';

    var obs = new MutationObserver(function () {
      var key = list.id || 'default';
      clearTimeout(verifiedBadgeDebounce[key]);
      verifiedBadgeDebounce[key] = setTimeout(function () {
        enhanceReviewWidget(root);
      }, 80);
    });
    obs.observe(list, { childList: true, subtree: true });
  }

  function formatRatingParts(rating) {
    var full = Math.floor(rating);
    var halfStar = full + 1;
    var frac = rating % 1;
    var x10 = Math.floor(rating * 10 + 0.5);
    return {
      full: full,
      halfStar: halfStar,
      frac: frac,
      display: Math.round(rating * 10) / 10,
      int: Math.floor(x10 / 10),
      dec: x10 % 10,
    };
  }

  function buildCardStarsHtml(rating, uid) {
    var parts = formatRatingParts(rating);
    var html = '';
    for (var i = 1; i <= 5; i++) {
      if (i <= parts.full) {
        html +=
          '<svg class="star-filled" viewBox="0 0 24 24" width="18" height="18" aria-hidden="true"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87L18.18 22 12 18.56 5.82 22 7 14.14l-5-4.87 6.91-1.01L12 2z"/></svg>';
      } else if (i === parts.halfStar && parts.frac >= 0.3) {
        html +=
          '<svg class="star-half" viewBox="0 0 24 24" width="18" height="18" aria-hidden="true"><defs><linearGradient id="cpr-api-half-' +
          uid +
          '-' +
          i +
          '"><stop offset="50%" stop-color="#f5a623"/><stop offset="50%" stop-color="#ddd"/></linearGradient></defs><path fill="url(#cpr-api-half-' +
          uid +
          '-' +
          i +
          ')" d="M12 2l3.09 6.26L22 9.27l-5 4.87L18.18 22 12 18.56 5.82 22 7 14.14l-5-4.87 6.91-1.01L12 2z"/></svg>';
      } else {
        html +=
          '<svg class="star-empty" viewBox="0 0 24 24" width="18" height="18" aria-hidden="true"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87L18.18 22 12 18.56 5.82 22 7 14.14l-5-4.87 6.91-1.01L12 2z"/></svg>';
      }
    }
    return html;
  }

  function buildPdpStarsHtml(rating, uid) {
    var parts = formatRatingParts(rating);
    var html = '';
    for (var i = 1; i <= 5; i++) {
      if (i <= parts.full) {
        html +=
          '<svg width="18" height="18" viewBox="0 0 24 24"><polygon points="12,2 15.09,8.26 22,9.27 17,14.14 18.18,21.02 12,17.77 5.82,21.02 7,14.14 2,9.27 8.91,8.26" fill="#f5a623"/></svg>';
      } else if (i === parts.halfStar && parts.frac >= 0.3) {
        html +=
          '<svg width="18" height="18" viewBox="0 0 24 24"><defs><linearGradient id="half-api-' +
          uid +
          '"><stop offset="50%" stop-color="#f5a623"/><stop offset="50%" stop-color="#ddd"/></linearGradient></defs><polygon points="12,2 15.09,8.26 22,9.27 17,14.14 18.18,21.02 12,17.77 5.82,21.02 7,14.14 2,9.27 8.91,8.26" fill="url(#half-api-' +
          uid +
          ')"/></svg>';
      } else {
        html +=
          '<svg width="18" height="18" viewBox="0 0 24 24"><polygon points="12,2 15.09,8.26 22,9.27 17,14.14 18.18,21.02 12,17.77 5.82,21.02 7,14.14 2,9.27 8.91,8.26" fill="#ddd"/></svg>';
      }
    }
    return html;
  }

  function syncPdpRating(productId, avg, count) {
    var mounts = document.querySelectorAll(
      '.cs-rating-amazon[data-product-id="' + productId + '"]'
    );

    var rating = parseFloat(avg);
    var reviewCount = parseInt(count, 10);
    if (!rating || !reviewCount || !mounts.length) return;

    var parts = formatRatingParts(rating);
    var starsHtml = buildPdpStarsHtml(rating, productId);

    mounts.forEach(function (mount) {
      mount.classList.remove('cs-rating-amazon--pending');
      mount.innerHTML =
        '<span class="cs-rating-amazon__value">' +
        parts.display +
        '</span>' +
        '<span class="cs-rating-amazon__stars">' +
        starsHtml +
        '</span>' +
        '<a href="#judgeme_product_reviews" class="cs-rating-amazon__count">(' +
        reviewCount +
        ')</a>';
      mount.dataset.synced = 'true';
    });
  }

  function syncCardRating(mount, avg, count) {
    if (!mount) return;

    var rating = parseFloat(avg);
    var reviewCount = parseInt(count, 10);
    if (!rating || !reviewCount) return;

    var productId = mount.getAttribute('data-product-id') || 'card';
    var sectionId = mount.getAttribute('data-section-id') || '0';
    var parts = formatRatingParts(rating);
    var uid = productId + '-' + sectionId;

    mount.classList.remove('card-bundle-aggregate-rating--pending');
    mount.innerHTML =
      '<span class="card-agg-stars" aria-label="' +
      parts.int +
      '.' +
      parts.dec +
      ' out of 5 stars">' +
      buildCardStarsHtml(rating, uid) +
      '</span>' +
      '<span class="card-agg-text"><strong>' +
      parts.int +
      '.' +
      parts.dec +
      '</strong> from ' +
      reviewCount +
      ' reviews</span>';
    mount.dataset.synced = 'true';
  }

  var pendingRatingInFlight = {};

  function hydratePendingRatings() {
    var productIds = {};
    document
      .querySelectorAll('.cs-rating-amazon[data-product-id], .card-bundle-aggregate-rating[data-product-id]')
      .forEach(function (el) {
        if (el.dataset.synced === 'true') return;
        productIds[el.getAttribute('data-product-id')] = true;
      });

    Object.keys(productIds).forEach(function (productId) {
      if (pendingRatingInFlight[productId]) return;
      pendingRatingInFlight[productId] = true;

      fetchJudgeMeWidgetData(productId, 1)
        .then(function (data) {
          var avg = data.average_rating;
          var count = data.number_of_reviews;
          if (!parseInt(count, 10)) return;

          syncPdpRating(productId, avg, count);
          document
            .querySelectorAll('.card-bundle-aggregate-rating[data-product-id="' + productId + '"]')
            .forEach(function (mount) {
              if (mount.dataset.synced === 'true') return;
              syncCardRating(mount, avg, count);
            });
        })
        .catch(function () {})
        .finally(function () {
          pendingRatingInFlight[productId] = false;
        });
    });
  }

  function histogramPct(row, totalReviews) {
    var pct = parseInt(row.percentage, 10);
    if (!isNaN(pct) && pct >= 0) return pct;
    var freq = parseInt(row.frequency, 10) || 0;
    if (!totalReviews || !freq) return 0;
    return Math.round((freq / totalReviews) * 100);
  }

  function buildReviewItemHtml(rev, index) {
    var initial = (rev.reviewer_name || 'V').trim().charAt(0).toUpperCase();
    return (
      '<div class="jdgm-rev jdgm-divider-top" data-rating="' +
      rev.rating +
      '">' +
      '<div class="jdgm-rev__header">' +
      '<div class="jdgm-rev__icon">' +
      initial +
      '</div>' +
      getStarsHtml(rev.rating) +
      '<span class="jdgm-rev__timestamp">' +
      formatReviewDate(rev.created_at) +
      '</span>' +
      '<span class="jdgm-rev__author-wrapper">' +
      '<span class="jdgm-rev__author">' +
      (rev.reviewer_name || 'Customer') +
      '</span>' +
      '</span></div>' +
      '<b class="jdgm-rev__title">' +
      (rev.title || '') +
      '</b>' +
      '<div class="jdgm-rev__body"><p class="jdgm-rev__body-text">' +
      stripHtml(rev.body_html) +
      '</p></div></div>'
    );
  }

  function appendReviewsToList(list, reviews) {
    list.innerHTML = '';
    reviews.forEach(function (rev, index) {
      list.insertAdjacentHTML('beforeend', buildReviewItemHtml(rev, index));
    });
  }

  function loadApiReviewPage(root, page) {
    var productId = root.dataset.jmProductId;
    if (!productId) return Promise.resolve(false);
    return fetchJudgeMeWidgetData(productId, page)
      .then(function (data) {
        var list = findReviewList(root);
        if (!list || !data.reviews || !data.reviews.length) return false;
        root.dataset.jmApiPage = String(page);
        appendReviewsToList(list, data.reviews);
        enhanceReviewWidget(root);
        return true;
      })
      .catch(function () {
        return false;
      });
  }

  function nativeWidgetReady(root) {
    if (!root) return false;
    var hasItems = root.querySelector('.jm-review-item, .jdgm-rev');
    if (!hasItems) return false;
    return (
      root.classList.contains('jdgm--done-setup-widget') ||
      root.querySelector('.jm-review-widget') != null ||
      root.querySelector('.jdgm-paginate, nav[aria-label*="Pagination" i]') != null
    );
  }

  function waitForNativeWidget(root, maxMs) {
    if (nativeWidgetReady(root)) return Promise.resolve(true);
    return new Promise(function (resolve) {
      var done = false;
      var deadline = Date.now() + (maxMs || NATIVE_WIDGET_WAIT_MS);

      function finish(hasNative) {
        if (done) return;
        done = true;
        obs.disconnect();
        resolve(hasNative);
      }

      function poll() {
        if (nativeWidgetReady(root)) {
          finish(true);
          return;
        }
        if (Date.now() >= deadline) {
          finish(false);
          return;
        }
        window.setTimeout(poll, 400);
      }

      var obs = new MutationObserver(function () {
        if (nativeWidgetReady(root)) finish(true);
      });
      obs.observe(root, { childList: true, subtree: true, attributes: true, attributeFilter: ['class'] });
      poll();
    });
  }

  function renderWidgetFromApi(root, data, productId) {
    var reviews = data.reviews || [];
    var total = parseInt(data.number_of_reviews, 10) || reviews.length;
    var avg = data.average_rating || '0';
    if (!total || !reviews.length) return false;

    var histogram = data.histogram || [];
    var histogramHtml = histogram
      .map(function (row) {
        var pct = histogramPct(row, total);
        return (
          '<div class="jdgm-histogram__row" data-rating="' +
          row.rating +
          '" data-frequency="' +
          row.frequency +
          '" data-percentage="' +
          pct +
          '">' +
          '<span class="jdgm-histogram__star" role="button" tabindex="0">' +
          '<span class="jdgm-star jdgm--on"></span>' +
          row.rating +
          ' star</span>' +
          '<span class="jdgm-histogram__bar">' +
          '<span class="jdgm-histogram__bar-content" style="width:' +
          pct +
          '%;background-color:' +
          HISTOGRAM_FILL +
          ';"></span></span>' +
          '<span class="jdgm-histogram__frequency">' +
          row.frequency +
          '</span></div>'
        );
      })
      .join('');

    root.innerHTML =
      '<h2 class="jdgm-widget-title">Customer Reviews</h2>' +
      '<div class="jdgm-rev-widg__header">' +
      '<div class="jdgm-rev-widg__summary">' +
      getSummaryStarsHtml(avg) +
      '<span class="jdgm-rev-widg__summary-text">Based on ' +
      total +
      ' reviews</span>' +
      '</div>' +
      '<div class="jdgm-histogram">' +
      histogramHtml +
      '</div>' +
      '<div class="jdgm-widget-actions-wrapper">' +
      '<a href="javascript:void(0)" class="jdgm-write-rev-link">Write a review</a>' +
      '</div>' +
      '</div>' +
      '<div class="jdgm-review-list"></div>';

    var list = root.querySelector('.jdgm-review-list');
    appendReviewsToList(list, reviews);

    root.dataset.jmProductId = productId || getWidgetProductId(root) || '';
    root.dataset.jmApiPage = '1';
    root.dataset.jmApiTotalPages = String(Math.max(1, Math.ceil(total / API_PER_PAGE)));
    root.classList.add('js-api-hydrated');
    root.removeAttribute('data-empty-state');
    return true;
  }

  function hydrateReviewWidget(root) {
    if (!root) return Promise.resolve(false);

    if (root.classList.contains('js-api-hydrated') && judgeMeAppPresent()) {
      var carousel = root.querySelector('.cs-jdgm-carousel');
      if (carousel) {
        var list = carousel.querySelector('.jdgm-review-list');
        if (list && carousel.parentNode) {
          carousel.parentNode.insertBefore(list, carousel);
        }
        carousel.remove();
      }
      root.classList.remove('js-api-hydrated');
      root.innerHTML = '';
      root.removeAttribute('data-jm-product-id');
      root.removeAttribute('data-jm-api-page');
      root.removeAttribute('data-jm-api-total-pages');
    } else if (root.classList.contains('js-api-hydrated')) {
      return Promise.resolve(false);
    }

    var productId = getWidgetProductId(root);
    if (!productId || hydrateInFlight[productId]) return Promise.resolve(false);

    if (nativeWidgetReady(root)) {
      enhanceReviewWidget(root);
      var embeddedNow =
        window.jdgm &&
        window.jdgm.data &&
        window.jdgm.data.reviewWidget &&
        window.jdgm.data.reviewWidget[productId];
      if (embeddedNow) {
        syncPdpRating(productId, embeddedNow.average_rating, embeddedNow.number_of_reviews);
      }
      return Promise.resolve(false);
    }

    hydrateInFlight[productId] = true;
    var nativeWaitMs = judgeMeAppPresent() ? NATIVE_WIDGET_WAIT_WITH_JDGM_MS : NATIVE_WIDGET_WAIT_MS;

    return waitForNativeWidget(root, nativeWaitMs)
      .then(function (hasNative) {
        if (hasNative || nativeWidgetReady(root)) {
          enhanceReviewWidget(root);
          return fetchJudgeMeWidgetData(productId, 1).then(function (data) {
            syncPdpRating(productId, data.average_rating, data.number_of_reviews);
            return false;
          });
        }

        return fetchJudgeMeWidgetData(productId, 1).then(function (data) {
          var apiCount = parseInt(data.number_of_reviews, 10) || 0;
          if (!apiCount) return false;

          if (window.jdgm && window.jdgm.data && window.jdgm.data.reviewWidget) {
            window.jdgm.data.reviewWidget[productId] = data;
          }

          syncPdpRating(productId, data.average_rating, data.number_of_reviews);

          if (nativeWidgetReady(root)) {
            enhanceReviewWidget(root);
            return false;
          }

          // Keep the native Judge.me widget when the app is installed — do not replace with a custom fallback UI.
          if (judgeMeAppPresent()) {
            return false;
          }

          return renderWidgetFromApi(root, data, productId);
        });
      })
      .catch(function () {
        return false;
      })
      .finally(function () {
        hydrateInFlight[productId] = false;
      });
  }

  function tryInit() {
    hydratePendingRatings();

    var roots = findReviewRoots();
    var jobs = roots.map(function (root) {
      return hydrateReviewWidget(root);
    });

    Promise.all(jobs).finally(function () {
      roots.forEach(function (root) {
        enhanceReviewWidget(root);
        setupVerifiedBadgeWatch(root);
        initCarousel(root);
      });
      hydratePendingRatings();
    });
  }

  function scheduleInit() {
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(tryInit, DEBOUNCE_MS);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scheduleInit);
  } else {
    scheduleInit();
  }

  var mo = new MutationObserver(scheduleInit);
  mo.observe(document.body, { childList: true, subtree: true });

})();
